/**
 * Pure presentation helpers for the Recent access card (f15.6). Every value
 * comes from the server's projection of the audit log; nothing is derived here
 * beyond wording.
 */

import type { AccessEventKind, ArtifactAccessEvent } from '@/api/client';

const WHEN = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

export const EVENT_BADGES: Record<AccessEventKind, { text: string; pill: string }> = {
  CODE_REDEEMED: { text: 'Code redeemed', pill: 'pill-info' },
  CODE_FAILED: { text: 'Code failed', pill: 'pill-danger' },
  BLOCKED: { text: 'Blocked — rate limit', pill: 'pill-warning' },
  VIEW: { text: 'View', pill: 'pill-info' },
  DOWNLOAD: { text: 'Download', pill: 'pill-success' },
  INTERNAL_DOWNLOAD: { text: 'Internal download', pill: 'pill-neutral' },
  INTERNAL_DENIED: { text: 'Download refused', pill: 'pill-danger' },
};

/** `d MMM HH:mm` in the browser's timezone, e.g. `18 Sep 16:04`. */
export function formatWhen(iso: string): string {
  return WHEN.format(new Date(iso)).replace(',', '');
}

/** The password label, "Signed-in user · {name}", or null for "unknown code". */
export function throughText(event: ArtifactAccessEvent): string | null {
  const through = event.through;
  if (!through) return null;
  return through.type === 'PASSWORD' ? through.label : `Signed-in user · ${through.display}`;
}

/** `v4`, or null when the entry names no version. */
export function versionText(event: ArtifactAccessEvent): string | null {
  return event.version?.number != null ? `v${event.version.number}` : null;
}

/** `Chrome / macOS · 203.0.113.x`, the IP part omitted when unknown. */
export function clientText(event: ArtifactAccessEvent): string {
  const { browser, os, ip_masked } = event.client;
  return ip_masked ? `${browser} / ${os} · ${ip_masked}` : `${browser} / ${os}`;
}
